import api from "@/api";

export interface SeoConfig {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  jsonLd?: string;
}

/**
 * Busca os metadados de SEO/GEO cadastrados no admin para uma rota do Nuxt
 * e injeta o JSON-LD (quando o admin fornecer). Páginas usam o retorno como
 * fonte primária e mantêm seus próprios valores estáticos como fallback (`||`).
 *
 * Espelha exatamente como blog/modelo já fazem SEO dinâmico hoje: a página
 * re-monta → re-busca → faz merge com o próprio fallback. Por isso é um
 * composable por página (não uma sobrescrita global no layout persistente,
 * que perderia a corrida com o useHead da página em navegação client-side).
 */
export async function useSeoConfig(routeName: string, language: string) {
  const key = `seo-${routeName}-${language}`;
  const asyncData = useAsyncData(key, async () => {
    try {
      const res = await api.get("v1/seo/getByRoute", {
        params: { route: routeName, language },
      });
      return (res.data?.seo as SeoConfig) ?? null;
    } catch {
      return null;
    }
  });
  const data = asyncData.data;

  // Injeção genérica de JSON-LD: o JSON-LD cadastrado no admin é a única fonte
  // (a home não tem mais fallback estático; backend é o responsável).
  //
  // IMPORTANTE (SSR): registrar o useHead AQUI, ANTES do `await` abaixo, e na
  // forma de FUNÇÃO. Num composable async, chamar useHead DEPOIS de um await
  // perde o contexto do componente e vira no-op no SSR (o <script> some). A
  // forma de função relê `data.value` no momento do render — quando o await já
  // populou o ref. Escapamos `<`/`>`/`&` para o conteúdo (vindo do admin) não
  // quebrar/escapar do <script> do documento.
  useHead(() => {
    const jsonLd = data.value?.jsonLd;
    return {
      script: jsonLd
        ? [
            {
              // `key` dá ao unhead um dedupe key determinístico (`script:seo-jsonld`),
              // fazendo o tag do SSR ser reconciliado (não reinjetado) na hidratação.
              // Sem isso, scripts inline (innerHTML) não são deduplicados e o
              // <script ld+json> aparece duplicado no DOM (ex.: Service x2 no validador).
              key: "seo-jsonld",
              type: "application/ld+json",
              innerHTML: jsonLd
                .replace(/</g, "\\u003c")
                .replace(/>/g, "\\u003e")
                .replace(/&/g, "\\u0026"),
            },
          ]
        : [],
    };
  });

  await asyncData; // bloqueia o SSR até o fetch resolver (data.value populado)

  return data; // Ref<SeoConfig | null>
}
